import { Component, OnInit } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { NgxPaginationModule } from 'ngx-pagination';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { saveAs } from 'file-saver';
import moment from 'moment';

import { User } from '../../../models/user.model';
import { AlertedDevice } from '../../../models/alerted-device.model';
import { AlertSummary } from '../../../models/alert-summary.model';
import { Group } from '../../../models/group.model';

import { AuthService } from '../../../services/auth.service';
import { DeviceService } from '../../../services/device.service';

declare var $: any;

import {
  ApexLegend,
  ApexPlotOptions,
  NgApexchartsModule,
  ApexDataLabels,
  ApexTooltip,
  ApexChart,
  ApexNonAxisChartSeries,
  ApexResponsive,
  ApexTitleSubtitle,
} from 'ng-apexcharts';
import { ConfirmServiceService } from '../../confirm-dialog/confirm-service.service';

export type CustomChartOptions = ChartOptions & {
  tooltip?: ApexTooltip;
};
export type ChartOptions = {
  chart: ApexChart;
  title: ApexTitleSubtitle;
  labels: string[];
  series: ApexNonAxisChartSeries;
  responsive: ApexResponsive[];
  plotOptions: ApexPlotOptions;
  colors: string[];
  legend: ApexLegend;
  dataLabels?: ApexDataLabels;
  noData: ApexNoData;
};
@Component({
  selector: 'app-alert-details',
  standalone: true,
  imports: [CommonModule, FormsModule, NgxPaginationModule, NgApexchartsModule],
  templateUrl: './alert-details.component.html',
  styleUrl: './alert-details.component.css',
})
export class AlertDetailsComponent implements OnInit {
  isAuthenticated: boolean = false;
  loggedUser: User = new User();
  alertedDevices: Array<AlertedDevice> = new Array<AlertedDevice>();
  alertSummary: AlertSummary = new AlertSummary();
  groups: Array<Group> = new Array<Group>();
  time_zone: String = Intl.DateTimeFormat().resolvedOptions().timeZone;
  selectedIds: Array<String> = new Array<String>();
  //pagination

  filter: any = {
    page: 1,
    limit: 5,
    group: '',
    consumer_id: '',
    time_zone: this.time_zone,
  };

  totalRecords: number = 0;

  chartOptions!: ChartOptions;
  chartSeries: number[] = [];
  chartLabels: string[] = [];
  chartColors: string[] = [];

  constructor(
    private authService: AuthService,
    private deviceService: DeviceService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private readonly confirmService: ConfirmServiceService
  ) {}
  ngOnInit(): void {
    this.loggedUser = this.authService.CurrentUserValue;
    this.filter.consumer_id =
      this.route.snapshot.paramMap.get('consumer_id') ?? '';
  }

  onSubmit(frm: NgForm): void {
    let credential = frm.value;
    credential.email = this.loggedUser.email;
    this.spinner.show();
    this.authService.authenticateToAlertPage(credential).subscribe(
      (response) => {
        this.isAuthenticated = true;
        this.getAllZone();
        this.getAllAlertedDevices();
        this.getAlertAggregation();
      },
      (error: any) => {
        this.toastr.error(error.error.message.Text, `Alert Details`);
        this.spinner.hide();
        console.error(error.error.message);
      }
    );
  }
  getAllAlertedDevices(): void {
    this.deviceService.getAllAlertedDevices(this.filter).subscribe(
      (response: any) => {
        this.alertedDevices = response.data?.devices ?? [];
        this.totalRecords = response.data?.totalRecords ?? 0;
        this.spinner.hide();
      },
      (error: any) => {
        this.toastr.error(error.error.message, `Alert Details`);
        this.spinner.hide();
        console.error(error.error.message);
      }
    );
  }

  pageChangeEvent(event: number) {
    this.spinner.show();
    this.filter.page = event;
    this.getAllAlertedDevices();
  }

  getAllZone() {
    this.deviceService.getAllZones().subscribe(
      (response: any) => {
        this.groups = response.data?.zones;
      },
      (err: any) => {
        console.log(err.error);
      }
    );
  }
  initDonutAlertChart(): void {
    this.chartOptions = {
      chart: {
        type: 'donut',
        width: '400',
        //height: 380,
      },
      title: {
        text: '',
      },
      legend: {
        position: 'right',
        offsetY: 30,
        height: 200,
      },
      labels: this.chartLabels,
      series: this.chartSeries,
      colors: this.chartColors,
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 200,
            },
            legend: {
              show: false,
            },
          },
        },
      ],
      plotOptions: {
        pie: {
          donut: {
            size: '50%',
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: function (val: string, opts) {
          return opts.w.globals.series[opts.seriesIndex];
        },
      },
      noData: {
        text: 'No alerts available',
        align: 'center',
        verticalAlign: 'middle',
        offsetX: 0,
        offsetY: 0,
      },
    };

    setTimeout(() => {
      $('div#div1').height($('div#div2').height());
    }, 350);
  }

  getAlertAggregation(): void {
    this.deviceService.getAlertAggregation(this.filter).subscribe(
      (response: any) => {
        this.alertSummary = response.data;
        this.chartSeries = [];
        this.chartLabels = [];
        this.chartColors = [];

        if (
          this.alertSummary?.magnetic_alert_sum != null &&
          this.alertSummary?.magnetic_alert_sum != 0
        ) {
          this.chartSeries.push(+this.alertSummary?.magnetic_alert_sum); // '+' This will convert any Number objects to primitive number values, resolving the type mismatch.
          this.chartLabels.push('Magnetic Attack');
          this.chartColors.push('#01a9ac');
        }
        if (
          this.alertSummary?.battery_low_alert_sum != null &&
          this.alertSummary?.battery_low_alert_sum != 0
        ) {
          this.chartSeries.push(+this.alertSummary?.battery_low_alert_sum);
          this.chartLabels.push('Battery Low');
          this.chartColors.push('#fe5d70');
        }
        if (
          this.alertSummary?.high_flow_alert_sum != null &&
          this.alertSummary?.high_flow_alert_sum != 0
        ) {
          this.chartSeries.push(+this.alertSummary?.high_flow_alert_sum);
          this.chartLabels.push('High Flow Rate');
          this.chartColors.push('#fe9365');
        }
        if (
          this.alertSummary?.low_flow_alert_sum != null &&
          this.alertSummary?.low_flow_alert_sum != 0
        ) {
          this.chartSeries.push(+this.alertSummary?.low_flow_alert_sum);
          this.chartLabels.push('Reverse Flow');
          this.chartColors.push('#d77dd3');
        }

        this.initDonutAlertChart();
      },
      (error: any) => {
        this.toastr.error(error.error.message, `Alert Details`);
        this.spinner.hide();
        console.error(error.error.message);
      }
    );
  }

  exportAlertedDevices(): void {
    if (this.alertedDevices.length) {
      this.spinner.show();

      this.deviceService.exportAlertedDevices(this.filter).subscribe(
        (data: Blob) => {
          const blob = new Blob([data], {
            //type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            type: 'application/octet-stream',
          });
          saveAs(blob, `Alerted_devices_${new Date().getTime()}.xlsx`);
          this.toastr.success(
            'Alerted devices exported successfully',
            'Alert Details'
          );
          this.spinner.hide();
        },
        (error) => {
          console.error('Error downloading the file', error);
          this.spinner.hide();
        }
      );
    } else {
      this.toastr.error('There are no alerts to export', 'Alert Details');
    }
  }

  exportDeviceAlerts(deviceId: string, dev_eui: string) {
    this.spinner.show();

    this.deviceService
      .exportDeviceAlerts({
        device_id: deviceId,
        consumer_id: this.filter.consumer_id,
        time_zone: this.time_zone,
      })
      .subscribe(
        (data: Blob) => {
          const blob = new Blob([data], {
            //type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            type: 'application/octet-stream',
          });
          saveAs(
            blob,
            `Alerts_${dev_eui}_${moment().format('DDMMYYYYHHmmss')}.xlsx`
          );
          this.toastr.success('Alerts exported successfully', 'Alert Details');
          this.spinner.hide();
        },
        (error) => {
          console.error('Error downloading the file', error);
          this.toastr.error('There are no alerts', `Alert Details`);
          this.spinner.hide();
        }
      );
  }

  excludeDevices(): void {
    const message = `Are you sure you want to delete the selected alerts?`;
    this.confirmService.showConfirm(
      message,
      () => {
        this.filter.device_ids = this.selectedIds.join(',');
        this.deviceService.excludeDevices(this.filter).subscribe(
          (response: any) => {
            this.filter.consumer_id = '';
            this.selectedIds = [];
            this.getAllAlertedDevices();
            this.getAlertAggregation();
            this.toastr.success(response.message.Text, 'Alert Details');
          },
          (error: any) => {
            this.toastr.error(error.error.message, `Alert Details`);
            this.spinner.hide();
            console.error(error.error.message);
          }
        );
      },
      () => {}
    );
  }

  excluddeAllDecices(): void {
    this.excludeDevices();
  }

  searchByFilter(): void {
    this.spinner.show();
    this.getAllAlertedDevices();
    this.getAlertAggregation();
  }
  clearFilter(): void {
    this.spinner.show();
    this.filter.group = '';
    this.filter.consumer_id = '';
    this.getAllAlertedDevices();
    this.getAlertAggregation();
  }
  onGroupSelectionChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.filter.group = target.value;
    this.getAllAlertedDevices();
    this.getAlertAggregation();
  }
  onDeviceIdSelected(deviceId: any): void {
    const index = this.selectedIds.indexOf(deviceId); // Check if the ID exists in the list
    if (index === -1) {
      // ID doesn't exist, so add it
      this.selectedIds.push(deviceId);
    } else {
      // ID exists, so remove it
      this.selectedIds.splice(index, 1);
    }
  }
}
